'use client';

import { useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { cn } from '@/lib/utils/cn';

type AdType =
  | 'push'
  | 'native'
  | '300x250'
  | '300x100'
  | '728x90'
  | 'socialBar'
  | 'native2'
  | '300x250-2'
  | '728x90-2'
  | '468x60-2'
  | '320x50-2';

interface AdSlotProps {
  type: AdType;
  className?: string;
}

interface AdConfig {
  iframeSrcdoc?: string;
  html?: string;
  width?: number;
  height?: number;
}

const AD_CONFIGS: Record<AdType, AdConfig> = {
  // ── Disabled ────────────────────────────────────────────
  push:      {},
  '300x100': {},
  '728x90':  {},

  // ── MagSrv ──────────────────────────────────────────────
  native: {
    html: `
      <ins class="eas6a97888e20" data-zoneid="5882480"></ins>
      <script src="https://a.magsrv.com/ad-provider.js" async></script>
    `,
  },
  '300x250': {
    html: `
      <ins class="eas6a97888e2" data-zoneid="5882472"></ins>
      <script src="https://a.magsrv.com/ad-provider.js" async></script>
    `,
    width: 300,
    height: 250,
  },

  // ── Adsterra ────────────────────────────────────────────
  socialBar: {
    html:`<script src="https://defrostgauntlet.com/08/9e/e6/089ee60688e74757b5607021620e6cfa.js"></script>`,
  },
  native2: {
    height: 250,
    iframeSrcdoc: `
      <html><body style="margin:0">
        <script async="async" data-cfasync="false" src="https://defrostgauntlet.com/3b63bfb299a993fab575b98c89b21f7f/invoke.js"></script>
        <div id="container-3b63bfb299a993fab575b98c89b21f7f"></div>
      </body></html>
    `,
  },
  '728x90-2': {
    width: 728,
    height: 90,
    iframeSrcdoc: `
      <html><body style="margin:0">
        <script>
          atOptions = {
            'key' : 'b310c2392c3964c9a0f09e5cd7ff43a2',
            'format' : 'iframe',
            'height' : 90,
            'width' : 728,
            'params' : {}
          };
        </script>
        <script async="async" src="https://defrostgauntlet.com/b310c2392c3964c9a0f09e5cd7ff43a2/invoke.js"></script>
      </body></html>
    `,
  },
  '468x60-2': {
    width: 468,
    height: 60,
    iframeSrcdoc: `
      <html><body style="margin:0">
        <script>
          atOptions = {
            'key' : '84a7562e58c7cfdb0cda7fb03afa6d2f',
            'format' : 'iframe',
            'height' : 60,
            'width' : 468,
            'params' : {}
          };
        </script>
        <script src="https://defrostgauntlet.com/84a7562e58c7cfdb0cda7fb03afa6d2f/invoke.js"></script>
      </body></html>
    `,
  },
  '320x50-2': {
    width: 320,
    height: 50,
    iframeSrcdoc: `
      <html><body style="margin:0">
        <script>
          atOptions = {
            'key' : '20caf84c070579dc4c42ed229dbbec2c',
            'format' : 'iframe',
            'height' : 50,
            'width' : 320,
            'params' : {}
          };
        </script>
        <script src="https://defrostgauntlet.com/20caf84c070579dc4c42ed229dbbec2c/invoke.js"></script>
      </body></html>
    `,
    // html: `<script async="async" src="https://defrostgauntlet.com/08/9e/e6/089ee60688e74757b5607021620e6cfa.js"></script>`,
  },
  '300x250-2': {
    width: 300,
    height: 250,
    iframeSrcdoc: `
      <html><body style="margin:0">
        <script>
          atOptions = {
            'key' : '71c0b33228273936524452a0a8cc84cf',
            'format' : 'iframe',
            'height' : 250,
            'width' : 300,
            'params' : {}
          };
        </script>
        <script async="async" src="https://defrostgauntlet.com/71c0b33228273936524452a0a8cc84cf/invoke.js"></script>
      </body></html>
    `,
  },
};

function resolveType(type: AdType, viewportWidth: number): AdType {
  switch (type) {
    case '728x90-2':
      if (viewportWidth >= 768) return '728x90-2';
      if (viewportWidth >= 480) return '468x60-2';
      return '320x50-2';
    case '468x60-2':
      if (viewportWidth >= 480) return '468x60-2';
      return '320x50-2';
    default:
      return type;
  }
}

// Inline style thay vì Tailwind để width/height luôn khớp chính xác với config
function getContainerStyle(config: AdConfig): React.CSSProperties {
  return {
    width:  config.width  ? `${config.width}px`  : '100%',
    height: config.height ? `${config.height}px` : 'auto',
  };
}

export function AdSlot({ type, className }: AdSlotProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const pathname = usePathname();

  const [resolvedType, setResolvedType] = useState<AdType>(() => {
    if (typeof window === 'undefined') return type;
    return resolveType(type, window.innerWidth);
  });

  useEffect(() => {
    const handler = () => {
      setResolvedType((prev) => {
        const next = resolveType(type, window.innerWidth);
        return prev !== next ? next : prev;
      });
    };
    handler();
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [type]);

  const config = AD_CONFIGS[resolvedType];

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    el.innerHTML = '';
    if (!config.html && !config.iframeSrcdoc) return;

    if (config.iframeSrcdoc) {
      const iframe = document.createElement('iframe');
      iframe.style.border  = 'none';
      iframe.style.display = 'block';
      iframe.width         = String(config.width  ?? '100%');
      iframe.height        = String(config.height ?? 'auto');
      iframe.setAttribute('scrolling', 'no');
      iframe.srcdoc = config.iframeSrcdoc;
      el.appendChild(iframe);
      return;
    }

    if (config.html) {
      const range = document.createRange();
      range.selectNodeContents(el);
      // kiểm tra xem el đã chèn script chưa, nếu chưa thì mới chèn, tránh trường hợp script bị chạy lại khi chỉ resize
      const hasScript = el.querySelector('script');
      if (hasScript) return;
      const fragment = range.createContextualFragment(config.html);
      el.appendChild(fragment);
    }
  }, [resolvedType, pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  
  return (
    <div
      key={resolvedType}           // force remount khi type thay đổi
      ref={containerRef}
      style={getContainerStyle(config)}   // width/height lấy thẳng từ config
      className={cn('overflow-hidden mx-auto', className)}
      aria-hidden="true"
    />
  );
}